// src/pages/ReportsAttendance.tsx
// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../components/Spinner";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import ErrorPage from "../components/ErrorPage";
import http from "../api/apiClient";
import { fetchAttendanceRecords } from "../api/attendance";
import { fetchSchoolYears } from "../utils/fetchSchoolYears";
import { formatDateDMY, parseToDate } from "../utils/dateUtils";
import { translateClassName } from "../utils/classroomUtils";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from "recharts";
import {
  FaCalendarAlt,
  FaChartLine,
  FaChevronDown,
  FaChevronRight,
  FaClipboardCheck,
  FaFilter,
} from "react-icons/fa";

const DEFAULT_RANGE_DAYS = 7;

const formatIsoDate = (date) => {
  const d = parseToDate(date);
  if (!d) return "";
  return d.toISOString().slice(0, 10);
};

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - DEFAULT_RANGE_DAYS);
  return {
    from: formatIsoDate(from),
    to: formatIsoDate(today),
  };
};

const normalizeId = (value) =>
  value == null ? "" : String(value).trim().toLowerCase();

const classroomBelongsToSchoolYear = (classroom, selectedYear) => {
  if (!classroom || !selectedYear) return false;

  const selectedIds = [
    selectedYear.id,
    selectedYear.uuid,
    selectedYear.schoolYearId,
    selectedYear.schoolYearUuid,
    selectedYear.__id,
  ]
    .map(normalizeId)
    .filter(Boolean);

  const classroomYearIds = [
    classroom.schoolYearId,
    classroom.schoolYearUuid,
    classroom.schoolYear?.id,
    classroom.schoolYear?.uuid,
    classroom.schoolyearId,
    classroom.schoolyearUuid,
    classroom.yearId,
    classroom.yearUuid,
    classroom.year?.id,
    classroom.year?.uuid,
  ]
    .map(normalizeId)
    .filter(Boolean);

  if (selectedIds.length > 0 && classroomYearIds.length > 0) {
    return classroomYearIds.some((id) => selectedIds.includes(id));
  }

  const selectedStart = parseToDate(selectedYear.startDate);
  const selectedEnd = parseToDate(selectedYear.endDate);
  const classroomStart = parseToDate(
    classroom.startDate ??
      classroom.schoolYearStartDate ??
      classroom.schoolYear?.startDate ??
      classroom.year?.startDate
  );
  const classroomEnd = parseToDate(
    classroom.endDate ??
      classroom.schoolYearEndDate ??
      classroom.schoolYear?.endDate ??
      classroom.year?.endDate
  );

  if (selectedStart && selectedEnd && classroomStart) {
    if (classroomStart < selectedStart || classroomStart > selectedEnd) {
      return false;
    }

    return !classroomEnd || classroomEnd <= selectedEnd;
  }

  const selectedStartYear = selectedStart?.getFullYear();
  const selectedEndYear = selectedEnd?.getFullYear();
  const yearLabel = String(
    classroom.schoolYearLabel ??
      classroom.schoolYearName ??
      classroom.yearLabel ??
      classroom.yearName ??
      ""
  );

  return Boolean(
    selectedStartYear &&
      selectedEndYear &&
      yearLabel.includes(String(selectedStartYear)) &&
      yearLabel.includes(String(selectedEndYear))
  );
};

const teacherRolePriority = (role) => {
  const normalized = String(role || "").toLowerCase();
  if (["primary", "lead", "mentor"].includes(normalized)) return 0;
  if (["assistant", "support"].includes(normalized)) return 1;
  return 2;
};

const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;

const joinNames = (...parts) => parts.filter(isNonEmptyString).join(" ");

const extractNameFromEntity = (entity, visited = new Set(), depth = 0) => {
  if (!entity || typeof entity !== "object" || depth > 4) return null;
  if (visited.has(entity)) return null;
  visited.add(entity);

  const preferredKeys = [
    "preferredName",
    "nickname",
    "displayName",
    "fullName",
    "name",
    "childName",
    "studentName",
  ];

  for (const key of preferredKeys) {
    if (isNonEmptyString(entity[key])) {
      return entity[key].trim();
    }
  }

  const combos = [
    [entity.firstName, entity.middleName, entity.lastName],
    [entity.firstName, entity.lastName],
    [entity.givenName, entity.familyName],
    [entity.childFirstName, entity.childMiddleName, entity.childLastName],
    [entity.childFirstName, entity.childLastName],
    [entity.studentFirstName, entity.studentLastName],
    [entity.personFirstName, entity.personLastName],
  ];

  for (const combo of combos) {
    const joined = joinNames(...combo);
    if (joined) return joined;
  }

  const nestedKeys = [
    "child",
    "student",
    "person",
    "profile",
    "user",
    "member",
    "participant",
    "data",
    "info",
  ];

  for (const key of nestedKeys) {
    const nested = entity[key];
    if (Array.isArray(nested)) {
      for (const candidate of nested) {
        const nestedName = extractNameFromEntity(candidate, visited, depth + 1);
        if (nestedName) return nestedName;
      }
    } else {
      const nestedName = extractNameFromEntity(nested, visited, depth + 1);
      if (nestedName) return nestedName;
    }
  }

  for (const value of Object.values(entity)) {
    if (typeof value === "object" && value !== null) {
      const nestedName = extractNameFromEntity(value, visited, depth + 1);
      if (nestedName) return nestedName;
    }
  }

  return null;
};

const buildClassroomLabel = (classroom, t) => {
  if (!classroom) return "-";
  const base =
    translateClassName(classroom) ||
    classroom.name ||
    classroom.displayName ||
    classroom.className ||
    `#${classroom.id ?? ""}`;

  const teachers = Array.isArray(classroom.teachers) ? classroom.teachers : [];
  if (teachers.length === 0) return base;

  const active = teachers
    .filter((teacher) => !teacher?.endDate)
    .sort(
      (a, b) =>
        teacherRolePriority(a?.role) - teacherRolePriority(b?.role)
    );
  if (active.length === 0) return base;
  const primary = active[0];
  const teacherName =
    primary.teacherName ||
    [primary.firstName, primary.lastName].filter(Boolean).join(" ") ||
    primary.displayName ||
    primary.username ||
    primary.email;
  if (!teacherName) return base;
  return `${base} – ${teacherName}`;
};

const buildStudentLabel = (record, t, childLookup) => {
  if (!record || typeof record !== "object") {
    return t("reports.attendance.table.unknown_student", {
      defaultValue: "Unknown student",
    });
  }

  const candidateObjects = [];
  const seen = new Set();
  const addCandidate = (candidate) => {
    if (!candidate || typeof candidate !== "object") return;
    if (seen.has(candidate)) return;
    seen.add(candidate);
    candidateObjects.push(candidate);
  };

  addCandidate(record);
  addCandidate(record.child);
  addCandidate(record.student);
  addCandidate(record.child?.child);
  addCandidate(record.child?.student);
  addCandidate(record.child?.person);
  addCandidate(record.student?.child);
  addCandidate(record.student?.person);
  if (childLookup) {
    const childIdCandidates = [
      record.childId,
      record.child?.id,
      record.child?.childId,
      record.child?.child?.id,
      record.studentId,
      record.student?.id,
      record.student?.studentId,
    ];
    for (const candidateId of childIdCandidates) {
      const normalizedId = normalizeId(candidateId);
      if (normalizedId && childLookup.has(normalizedId)) {
        const lookupChild = childLookup.get(normalizedId);
        addCandidate(lookupChild);
        addCandidate(lookupChild?.child);
        addCandidate(lookupChild?.student);
        addCandidate(lookupChild?.person);
      }
    }
  }

  const directStrings = [
    record.childName,
    record.studentName,
    record.studentDisplayName,
    record.displayName,
    record.fullName,
    record.name,
    joinNames(record.firstName, record.middleName, record.lastName),
    joinNames(record.childFirstName, record.childMiddleName, record.childLastName),
    joinNames(record.studentFirstName, record.studentLastName),
  ];

  for (const direct of directStrings) {
    if (isNonEmptyString(direct)) {
      return direct.trim();
    }
  }

  for (const candidate of candidateObjects) {
    const name = extractNameFromEntity(candidate);
    if (name) return name;
  }

  const fallbackId =
    record.childId ??
    record.child?.id ??
    record.studentId ??
    record.child?.childId ??
    record.child?.child?.id ??
    record.student?.id ??
    null;

  if (fallbackId != null) {
    return `#${fallbackId}`;
  }

  return t("reports.attendance.table.unknown_student", {
    defaultValue: "Unknown student",
  });
};

const deriveStatusKey = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "PRESENT") return "present";
  if (normalized === "ABSENT") return "absent";
  if (normalized === "EXCUSED") return "excused";
  if (normalized === "LATE") return "late";
  if (!normalized || normalized === "X" || normalized === "UNKNOWN") return "unmarked";
  return "other";
};

const getDateKey = (value) => {
  const date = parseToDate(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getEmptyStats = () => ({
  present: 0,
  absent: 0,
  excused: 0,
  late: 0,
  unmarked: 0,
  other: 0,
  total: 0,
});

const BORDER_CLASS_BY_SERIES = {
  present: "border-green-400 dark:border-green-500",
  absent: "border-red-400 dark:border-red-500",
  excused: "border-yellow-400 dark:border-yellow-500",
  late: "border-purple-400 dark:border-purple-500",
};

const buildChildLookupMap = (children) => {
  const map = new Map();
  if (!Array.isArray(children)) return map;
  children.forEach((child) => {
    const rawId =
      child?.id ??
      child?.childId ??
      child?.child?.id ??
      child?.child?.childId ??
      child?.studentId ??
      null;
    if (rawId != null) {
      const normalized = normalizeId(rawId);
      if (normalized) {
        map.set(normalized, child);
      }
    }
  });
  return map;
};

const deriveStudentKey = (record) => {
  if (!record || typeof record !== "object") return `record::unknown`;
  const idCandidates = [
    record.childId,
    record.child?.id,
    record.child?.childId,
    record.child?.child?.id,
    record.studentId,
    record.student?.id,
    record.student?.studentId,
    record.id,
    record.recordId,
  ];
  for (const candidate of idCandidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return `id::${normalized}`;
  }

  const nameCandidates = [
    record.childName,
    record.studentName,
    record.studentDisplayName,
    record.displayName,
    record.fullName,
    joinNames(record.firstName, record.middleName, record.lastName),
  ];
  for (const name of nameCandidates) {
    if (isNonEmptyString(name)) {
      return `name::${name.trim().toLowerCase()}`;
    }
  }

  const fallbackString = [
    record.classroomId,
    record.attendanceDate,
    record.date,
    record.childName,
    record.studentName,
    record.id,
    record.recordId,
  ]
    .filter((part) => part != null)
    .map((part) => String(part))
    .join("|");

  const normalizedFallback = normalizeId(fallbackString);
  return `record::${normalizedFallback || "unknown"}`;
};

const Pill = ({ tone = "gray", label, value, srLabel }) => {
  if (!value) return null;
  const toneMap = {
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    red:     "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    amber:   "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    violet:  "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
    gray:    "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${toneMap[tone] || toneMap.gray}`}
      aria-label={srLabel ? `${srLabel}: ${value}` : undefined}
      title={srLabel ? `${srLabel}: ${value}` : label}
    >
      <span className="font-semibold">{value}</span>
      {label ? <span className="ml-1">{label}</span> : null}
    </span>
  );
};

// Klikbaar + active styling
const StudentListItem = ({ name, stats, t, onClick, isActive }) => (
  <li
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
    className={[
      "flex items-center justify-between gap-3",
      "border-b border-gray-200 dark:border-gray-700",
      "border-l-[1.5px] border-l-gray-300 dark:border-l-gray-700",
      "pl-2 py-2 last:border-b-0 cursor-pointer select-none",
      "hover:bg-gray-50 dark:hover:bg-gray-800/60",
      isActive ? "ring-2 ring-indigo-400 ring-offset-1 dark:ring-offset-gray-900" : ""
    ].join(" ")}
  >
    <span className="truncate text-sm text-gray-900 dark:text-gray-100">{name}</span>
    <div className="flex flex-wrap items-center gap-1.5">
      <Pill
        tone="emerald"
        value={stats.present}
        label={t("reports.attendance.summary.present", { defaultValue: "Present" })}
        srLabel={t("reports.attendance.summary.present", { defaultValue: "Present" })}
      />
      <Pill
        tone="red"
        value={stats.absent}
        label={t("reports.attendance.summary.absent", { defaultValue: "Absent" })}
        srLabel={t("reports.attendance.summary.absent", { defaultValue: "Absent" })}
      />
      <Pill
        tone="amber"
        value={stats.excused}
        label={t("reports.attendance.summary.excused", { defaultValue: "Excused" })}
        srLabel={t("reports.attendance.summary.excused", { defaultValue: "Excused" })}
      />
      <Pill
        tone="violet"
        value={stats.late}
        label={t("reports.attendance.summary.late", { defaultValue: "Late" })}
        srLabel={t("reports.attendance.summary.late", { defaultValue: "Late" })}
      />
      {stats.unmarked > 0 && (
        <Pill
          tone="gray"
          value={stats.unmarked}
          label={t("reports.attendance.summary.unmarked", { defaultValue: "Unmarked" })}
          srLabel={t("reports.attendance.summary.unmarked", { defaultValue: "Unmarked" })}
        />
      )}
      {stats.other > 0 && (
        <Pill
          tone="gray"
          value={stats.other}
          label={t("reports.attendance.summary.other", { defaultValue: "Other" })}
          srLabel={t("reports.attendance.summary.other", { defaultValue: "Other" })}
        />
      )}
    </div>
  </li>
);

const ReportsAttendance = () => {
  const { t } = useTranslation();

  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState("");
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState(null);

  const [classrooms, setClassrooms] = useState([]);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const [classroomsError, setClassroomsError] = useState(null);

  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState(null);
  const [records, setRecords] = useState([]);

  const [selectedClassroomFilter, setSelectedClassroomFilter] = useState("");
  const [highlightedSeries, setHighlightedSeries] = useState(null);
  const [expandedClassrooms, setExpandedClassrooms] = useState(() => new Set());
  const [childrenByClass, setChildrenByClass] = useState(() => new Map());
  const [childrenLoading, setChildrenLoading] = useState(() => new Set());
  const [childrenErrors, setChildrenErrors] = useState(() => new Map());

  // NEW: geselecteerde leerling
  const [selectedStudent, setSelectedStudent] = useState(null);
  const handleStudentClick = useCallback((studentKey) => {
    setSelectedStudent((prev) => (prev === studentKey ? null : studentKey));
  }, []);

  useEffect(() => {
    const loadYears = async () => {
      setYearsLoading(true);
      setYearsError(null);
      try {
        const list = await fetchSchoolYears();
        const normalized = Array.isArray(list) ? list : [];
        setYears(normalized);
        if (normalized.length > 0 && !selectedYearId) {
          const today = new Date();
          const current =
            normalized.find((year) => {
              const start = parseToDate(year.startDate);
              const end = parseToDate(year.endDate);
              if (!start || !end) return false;
              return start <= today && today <= end;
            }) ?? normalized[0];
          setSelectedYearId(String(current?.id ?? ""));
        }
      } catch (err) {
        setYearsError(
          t("attendance.admin.load_years_failed", {
            defaultValue: "Could not load school years.",
          })
        );
      } finally {
        setYearsLoading(false);
      }
    };

    loadYears();
  }, [t]);

  useEffect(() => {
    setChildrenByClass(new Map());
    setChildrenLoading(new Set());
    setChildrenErrors(new Map());
    setExpandedClassrooms(new Set());
  }, [selectedYearId]);

  useEffect(() => {
    if (!selectedYearId || years.length === 0) {
      setClassrooms([]);
      return;
    }

    let cancelled = false;

    const loadClassrooms = async () => {
      setClassroomsLoading(true);
      setClassroomsError(null);

      try {
        const selectedYear =
          years.find((year) => {
            const id = normalizeId(
              year?.id ??
                year?.uuid ??
                year?.schoolYearId ??
                year?.schoolYearUuid ??
                year?.__id
            );
            return id === normalizeId(selectedYearId);
          }) ?? null;

        const { data } = await http.get("/classrooms", {
          params: { schoolYearId: selectedYearId },
        });

        if (cancelled) return;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.content)
            ? data.content
            : Array.isArray(data?.items)
              ? data.items
              : [];

        const scoped = selectedYear
          ? list.filter((classroom) =>
              classroomBelongsToSchoolYear(classroom, selectedYear)
            )
          : [];

        const unique = new Map();

        scoped.forEach((classroom) => {
          const rawId =
            classroom?.id ??
            classroom?.uuid ??
            classroom?.classroomId ??
            classroom?.classroomUuid ??
            null;

          const key = normalizeId(rawId);
          if (!key || unique.has(key)) return;
          unique.set(key, classroom);
        });

        const sorted = Array.from(unique.values()).sort((a, b) => {
          const aStart = parseToDate(a?.startDate)?.getTime();
          const bStart = parseToDate(b?.startDate)?.getTime();

          if (
            Number.isFinite(aStart) &&
            Number.isFinite(bStart) &&
            aStart !== bStart
          ) {
            return aStart - bStart;
          }

          return buildClassroomLabel(a, t).localeCompare(
            buildClassroomLabel(b, t),
            undefined,
            { numeric: true, sensitivity: "base" }
          );
        });

        setClassrooms(sorted);
      } catch (err) {
        if (cancelled) return;

        setClassroomsError(
          t("attendance.admin.load_classrooms_failed", {
            defaultValue: "Could not load classrooms.",
          })
        );
        setClassrooms([]);
      } finally {
        if (!cancelled) {
          setClassroomsLoading(false);
        }
      }
    };

    loadClassrooms();

    return () => {
      cancelled = true;
    };
  }, [selectedYearId, t, years]);

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;

    const loadRecords = async () => {
      setRecordsLoading(true);
      setRecordsError(null);
      try {
        const data = await fetchAttendanceRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
        });
        setRecords(Array.isArray(data) ? data : []);
      } catch (err) {
        setRecordsError(
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.admin.load_records_failed", {
                defaultValue: "Could not load attendance records.",
              })
        );
      } finally {
        setRecordsLoading(false);
      }
    };

    loadRecords();
  }, [dateRange.from, dateRange.to, t]);

  const fetchChildrenForClass = useCallback(
    async (rawClassroomId, normalizedClassroomId) => {
      const normalized = normalizedClassroomId ?? normalizeId(rawClassroomId);
      if (!normalized) return;
      if (childrenByClass.has(normalized)) return;

      setChildrenLoading((prev) => {
        const next = new Set(prev);
        next.add(normalized);
        return next;
      });

      try {
        const response = await http.get(`/classrooms/${rawClassroomId}/children`, {
          validateStatus: (status) => [200, 204, 404].includes(status),
        });
        const list =
          response.status === 200 && Array.isArray(response.data)
            ? response.data
            : [];
        setChildrenByClass((prev) => {
          const next = new Map(prev);
          next.set(normalized, list);
          return next;
        });
        setChildrenErrors((prev) => {
          const next = new Map(prev);
          next.delete(normalized);
          return next;
        });
      } catch (error) {
        setChildrenErrors((prev) => {
          const next = new Map(prev);
          next.set(
            normalized,
            t("reports.attendance.table.children_load_failed", {
              defaultValue: "Could not load student information.",
            })
          );
          return next;
        });
      } finally {
        setChildrenLoading((prev) => {
          const next = new Set(prev);
          next.delete(normalized);
          return next;
        });
      }
    },
    [childrenByClass, t]
  );

  const classroomMap = useMemo(() => {
    const map = new Map();
    classrooms.forEach((cls) => {
      const id = normalizeId(cls?.id);
      if (!id) return;
      map.set(id, cls);
    });
    return map;
  }, [classrooms]);

  const relevantRecords = useMemo(() => {
    if (!selectedYearId) return [];
    return records.filter((record) => classroomMap.has(normalizeId(record?.classroomId)));
  }, [records, classroomMap, selectedYearId]);

  const aggregatedByClass = useMemo(() => {
    const grouped = new Map();
    relevantRecords.forEach((record) => {
      const classroomId = normalizeId(record?.classroomId);
      if (!classroomId) return;
      if (!classroomMap.has(classroomId)) return;
      if (!grouped.has(classroomId)) {
        grouped.set(classroomId, { stats: getEmptyStats() });
      }
      const entry = grouped.get(classroomId);
      const key = deriveStatusKey(record?.status);
      entry.stats[key] = (entry.stats[key] || 0) + 1;
      entry.stats.total += 1;
    });
    return grouped;
  }, [relevantRecords, classroomMap]);

  const overallStats = useMemo(() => {
    const totals = getEmptyStats();
    aggregatedByClass.forEach(({ stats }) => {
      Object.keys(totals).forEach((key) => {
        if (typeof stats[key] === "number") {
          totals[key] += stats[key];
        }
      });
    });
    return totals;
  }, [aggregatedByClass]);

  const classRows = useMemo(() => {
    const rows = [];
    aggregatedByClass.forEach(({ stats }, classroomId) => {
      const cls = classroomMap.get(classroomId);
      rows.push({
        classroomId,
        rawClassroomId: cls?.id ?? cls?.classroomId ?? classroomId,
        label: buildClassroomLabel(cls, t),
        stats,
      });
    });
    rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
    return rows;
  }, [aggregatedByClass, classroomMap, t]);

  const filteredRows = useMemo(() => {
    if (!selectedClassroomFilter) return classRows;
    const normalized = normalizeId(selectedClassroomFilter);
    return classRows.filter((row) => row.classroomId === normalized);
  }, [classRows, selectedClassroomFilter]);

  const filteredRecords = useMemo(() => {
    if (!selectedClassroomFilter) return relevantRecords;
    const normalized = normalizeId(selectedClassroomFilter);
    return relevantRecords.filter(
      (record) => normalizeId(record?.classroomId) === normalized
    );
  }, [relevantRecords, selectedClassroomFilter]);

  const filteredOverall = useMemo(() => {
    if (!selectedClassroomFilter) return overallStats;
    const totals = getEmptyStats();
    filteredRows.forEach(({ stats }) => {
      Object.keys(totals).forEach((key) => {
        if (typeof stats[key] === "number") {
          totals[key] += stats[key];
        }
      });
    });
    return totals;
  }, [filteredRows, overallStats, selectedClassroomFilter]);

  const dailySeries = useMemo(() => {
    const grouped = new Map();
    filteredRecords.forEach((record) => {
      const key =
        getDateKey(record?.date) ||
        getDateKey(record?.attendanceDate) ||
        getDateKey(record?.day);
      if (!key) return;
      if (!grouped.has(key)) {
        grouped.set(key, { date: key, stats: getEmptyStats() });
      }
      const entry = grouped.get(key);
      const statusKey = deriveStatusKey(record?.status);
      entry.stats[statusKey] = (entry.stats[statusKey] || 0) + 1;
      entry.stats.total += 1;
    });
    return Array.from(grouped.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(({ date, stats }) => ({
        date,
        present: stats.present,
        absent: stats.absent,
        excused: stats.excused,
        late: stats.late,
        unmarked: stats.unmarked,
        other: stats.other,
        total: stats.total,
      }));
  }, [filteredRecords]);

  const formatChartDate = (value) => {
    const parsed = parseToDate(value);
    if (!parsed) return value;
    return parsed.toLocaleDateString("nl-NL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const chartSeries = useMemo(
    () => [
      {
        key: "present",
        color: "#10B981",
        name: t("reports.attendance.summary.present", { defaultValue: "Present" }),
      },
      {
        key: "absent",
        color: "#EF4444",
        name: t("reports.attendance.summary.absent", { defaultValue: "Absent" }),
      },
      {
        key: "excused",
        color: "#F59E0B",
        name: t("reports.attendance.summary.excused", { defaultValue: "Excused" }),
      },
      {
        key: "late",
        color: "#8B5CF6",
        name: t("reports.attendance.summary.late", { defaultValue: "Late" }),
      },
    ],
    [t]
  );

  // NEW: dataset voor geselecteerde leerling
  const selectedStudentSeries = useMemo(() => {
    if (!selectedStudent) return null;

    const grouped = new Map();
    filteredRecords.forEach((record) => {
      if (deriveStudentKey(record) !== selectedStudent) return;
      const key =
        getDateKey(record?.date) ||
        getDateKey(record?.attendanceDate) ||
        getDateKey(record?.day);
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, { date: key, stats: getEmptyStats() });
      const entry = grouped.get(key);
      const statusKey = deriveStatusKey(record?.status);
      entry.stats[statusKey] = (entry.stats[statusKey] || 0) + 1;
      entry.stats.total += 1;
    });

    return Array.from(grouped.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(({ date, stats }) => ({
        date,
        present: stats.present,
        absent: stats.absent,
        excused: stats.excused,
        late: stats.late,
        unmarked: stats.unmarked,
        other: stats.other,
        total: stats.total,
      }));
  }, [filteredRecords, selectedStudent]);

  const tooltipFormatter = (value, key) => {
    const series = chartSeries.find((item) => item.key === key);
    return [value, series?.name ?? key];
  };

  const studentStatsByClass = useMemo(() => {
    const classMap = new Map();

    filteredRecords.forEach((record) => {
      const classroomId = normalizeId(record?.classroomId);
      if (!classroomId) return;

      if (!classMap.has(classroomId)) {
        classMap.set(classroomId, new Map());
      }
      const studentMap = classMap.get(classroomId);

      const childIdRaw =
        record?.childId ??
        record?.child?.id ??
        record?.child?.childId ??
        record?.child?.child?.id ??
        record?.studentId ??
        record?.student?.id ??
        record?.student?.studentId ??
        null;

      const studentKey = deriveStudentKey(record);

      if (!studentMap.has(studentKey)) {
        studentMap.set(studentKey, {
          studentKey,
          childId: childIdRaw,
          stats: getEmptyStats(),
          sampleRecord: record,
          fallbackLabel: buildStudentLabel(record, t),
        });
      }

      const entry = studentMap.get(studentKey);
      if (!entry.sampleRecord && record) {
        entry.sampleRecord = record;
      }
      if (childIdRaw != null && entry.childId == null) {
        entry.childId = childIdRaw;
      }
      const statusKey = deriveStatusKey(record?.status);
      entry.stats[statusKey] = (entry.stats[statusKey] || 0) + 1;
      entry.stats.total += 1;
    });

    const result = new Map();
    classMap.forEach((studentsMap, classroomId) => {
      const rows = Array.from(studentsMap.values()).sort((a, b) =>
        (a.fallbackLabel || "").localeCompare(b.fallbackLabel || "", undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
      result.set(classroomId, rows);
    });
    return result;
  }, [filteredRecords, t]);

  const summaryCards = useMemo(() => {
    const cards = chartSeries.map(({ key, name, color }) => ({
      seriesKey: key,
      label: name,
      value: filteredOverall[key] ?? 0,
      borderClass: BORDER_CLASS_BY_SERIES[key] ?? "border-gray-300",
      accentColor: color,
    }));

    cards.push({
      seriesKey: null,
      label: t("reports.attendance.summary.total", { defaultValue: "Total" }),
      value: filteredOverall.total,
      borderClass: "border-indigo-400 dark:border-indigo-500",
      accentColor: "#6366F1",
    });

    return cards;
  }, [chartSeries, filteredOverall, t]);

  const toggleClassroomExpansion = (classroomId, rawClassroomId) => {
    const normalizedId = normalizeId(classroomId);
    const isCurrentlyExpanded = expandedClassrooms.has(normalizedId);
    if (!isCurrentlyExpanded) {
      if (
        normalizedId &&
        !childrenByClass.has(normalizedId) &&
        !childrenLoading.has(normalizedId)
      ) {
        const targetClassroom = rawClassroomId ?? classroomId;
        if (targetClassroom != null) {
          fetchChildrenForClass(targetClassroom, normalizedId);
        }
      }
    }
    setExpandedClassrooms((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedId)) {
        next.delete(normalizedId);
      } else {
        next.add(normalizedId);
      }
      return next;
    });
  };

  const schoolYearOptions = useMemo(
    () =>
      years.map((year) => {
        const id = String(year?.id ?? year?.__id ?? "");
        const startDate = parseToDate(year?.startDate);
        const endDate = parseToDate(year?.endDate);
        const label =
          startDate && endDate
            ? `${startDate.getFullYear()}/${endDate.getFullYear()}`
            : year?.name || year?.label || id;

        return {
          ...year,
          id,
          name: label,
          label,
        };
      }),
    [years]
  );

  if (yearsLoading) {
    return <Spinner />;
  }

  if (yearsError) {
    return <ErrorPage message={yearsError} onRetry={() => window.location.reload()} />;
  }

  const isBusy = recordsLoading || classroomsLoading;

  return (
    <main className="lds-report-enter px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
        <div className="space-y-6">
          <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-7 shadow-sm dark:border-indigo-900 dark:from-indigo-950/50 dark:via-slate-950 dark:to-sky-950/40 sm:px-8">
            <FaClipboardCheck
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-indigo-200 dark:text-indigo-900/40 sm:block"
            />

            <div className="relative z-10 flex min-w-0 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <FaClipboardCheck aria-hidden="true" />
              </div>

              <div className="min-w-0 max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                  {t("reports.title", { defaultValue: "Reports" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
                  {t("reports.attendance.title", {
                    defaultValue: "Attendance report",
                  })}
                </h1>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  {t("reports.attendance.description", {
                    defaultValue:
                      "Review attendance trends by school year, date range, classroom and student.",
                  })}
                </p>
              </div>
            </div>
          </section>

          <section className="overflow-visible rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-3 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:px-6">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FaFilter aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  {t("reports.attendance.filters.title", {
                    defaultValue: "Search and filters",
                  })}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t("reports.attendance.filters.description", {
                    defaultValue:
                      "Filter attendance by school year, date range and classroom.",
                  })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2 xl:grid-cols-4 lg:px-6">
              <div className="min-w-0">
                <SchoolYearDropdown
                  schoolYears={schoolYearOptions}
                  selectedSchoolYearId={selectedYearId}
                  onChange={(value) => {
                    setSelectedYearId(String(value || ""));
                    setSelectedClassroomFilter("");
                  }}
                  label={t("reports.attendance.filters.school_year", {
                    defaultValue: "School year",
                  })}
                />
              </div>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <FaCalendarAlt aria-hidden="true" />
                  {t("reports.attendance.filters.from", { defaultValue: "From" })}
                </span>
                <input
                  type="date"
                  className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                  value={dateRange.from}
                  max={dateRange.to || undefined}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <FaCalendarAlt aria-hidden="true" />
                  {t("reports.attendance.filters.to", { defaultValue: "To" })}
                </span>
                <input
                  type="date"
                  className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                  value={dateRange.to}
                  min={dateRange.from || undefined}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("reports.attendance.filters.classroom", {
                    defaultValue: "Classroom",
                  })}
                </span>
                <select
                  className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                  value={selectedClassroomFilter}
                  onChange={(e) => setSelectedClassroomFilter(e.target.value)}
                >
                  <option value="">
                    {t("reports.attendance.filters.all_classes", {
                      defaultValue: "All classrooms",
                    })}
                  </option>
                  {classrooms.map((classroom) => (
                    <option key={classroom.id} value={classroom.id}>
                      {buildClassroomLabel(classroom, t)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

        {classroomsError && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-200">
            {classroomsError}
          </div>
        )}

        {recordsError && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-200">
            {recordsError}
          </div>
        )}

        {isBusy ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-indigo-100/70 px-4 py-6 text-center text-sm text-gray-500 dark:border-indigo-500/40 dark:text-gray-300">
            {t("reports.attendance.no_data", {
              defaultValue: "No attendance data was found for the selected filters.",
            })}
          </div>
        ) : (
          <>
            {dailySeries.length > 0 && (
              <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950 sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300"><FaChartLine aria-hidden="true" /></div>
                  <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t("reports.attendance.trends.title", { defaultValue: "Daily trend" })}
                  </h2>

                  {/* NEW: indicator + reset bij leerlingfilter */}
                  {selectedStudent && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span className="rounded bg-indigo-50 px-2 py-1 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                        {t("reports.attendance.trends.filtered_by_student", { defaultValue: "Filtered by student" })}
                      </span>
                      <button
                        type="button"
                        className="text-indigo-600 underline hover:no-underline dark:text-indigo-300"
                        onClick={() => setSelectedStudent(null)}
                      >
                        {t("common.clear_filter", { defaultValue: "Clear filter" })}
                      </button>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t("reports.attendance.trends.subtitle", {
                      defaultValue: "Select a summary card below to highlight that series.",
                    })}
                  </p>
                  </div>
                </div>
                <div className="mt-4 h-72 w-full rounded-2xl bg-white/80 p-4 shadow-inner dark:bg-gray-900/80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={selectedStudent ? (selectedStudentSeries ?? []) : dailySeries}
                      margin={{ top: 5, right: 16, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E0E7FF" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatChartDate}
                        stroke="#6B7280"
                        fontSize={12}
                      />
                      <YAxis allowDecimals={false} stroke="#6B7280" fontSize={12} />
                      <Tooltip
                        labelFormatter={(value) =>
                          t("reports.attendance.trends.tooltip_label", {
                            defaultValue: formatDateDMY(value),
                            date: formatDateDMY(value),
                          })
                        }
                        formatter={tooltipFormatter}
                      />
                      <Legend />
                      {chartSeries.map(({ key, color, name }) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={color}
                          name={name}
                          strokeWidth={highlightedSeries === key ? 3 : 2}
                          strokeOpacity={
                            highlightedSeries && highlightedSeries !== key ? 0.25 : 1
                          }
                          dot={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
              {summaryCards.map(({ seriesKey, label, value, borderClass, accentColor }) => {
                const interactive = seriesKey !== null;
                const isActive = interactive && highlightedSeries === seriesKey;
                const isDimmed = interactive && highlightedSeries && highlightedSeries !== seriesKey;
                const baseClasses =
                  "rounded-[20px] border border-slate-200/80 bg-white p-4 text-left shadow-sm transition dark:border-slate-800 dark:bg-slate-950";
                const accentClasses = `border-l-4 ${borderClass}`;
                const interactiveClasses = interactive
                  ? "cursor-pointer hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
                  : "";
                const activeClasses = isActive ? "ring-2 ring-offset-2" : "";
                const dimmedClasses = isDimmed ? "opacity-60" : "";
                const gradientStyle =
                  accentColor && accentColor.startsWith("#")
                    ? {
                        backgroundImage: `linear-gradient(135deg, ${accentColor}08, ${accentColor}12)`,
                        borderColor: `${accentColor}26`,
                      }
                    : {"backgroundImage":"linear-gradient(135deg, rgba(79,70,229,0.04), rgba(79,70,229,0.08))","borderColor":"rgba(79,70,229,0.15)"};
                const interactiveStyle =
                  accentColor && interactive
                    ? {
                        "--tw-ring-color": accentColor,
                        ...(isActive ? { boxShadow: `0 0 0 2px ${accentColor}33` } : {}),
                      }
                    : isActive && accentColor
                    ? { boxShadow: `0 0 0 2px ${accentColor}33` }
                    : {};
                const style =
                  accentColor || isActive
                    ? {
                        ...gradientStyle,
                        ...interactiveStyle,
                      }
                    : gradientStyle;

                if (interactive) {
                  return (
                    <button
                      key={seriesKey}
                      type="button"
                      className={`${baseClasses} ${accentClasses} ${interactiveClasses} ${activeClasses} ${dimmedClasses}`}
                      style={style}
                      onClick={() =>
                        setHighlightedSeries((prev) => (prev === seriesKey ? null : seriesKey))
                      }
                      aria-pressed={isActive}
                    >
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{label}</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
                    </button>
                  );
                }

                return (
                  <div key="total" className={`${baseClasses} ${accentClasses} ${dimmedClasses}`}>
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{label}</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
                  </div>
                );
              })}
            </div>

            <LdsTableCard>
              <LdsTableScroll>
                <LdsTable className="min-w-[900px]">
                  <LdsTableHead>
                    <LdsTableRow>
                    <LdsTableHeadCell>
                      <span className="sr-only">
                        {t("reports.attendance.table.expand", { defaultValue: "Expand" })}
                      </span>
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>
                      {t("reports.attendance.table.class", { defaultValue: "Classroom" })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("reports.attendance.summary.present", { defaultValue: "Present" })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("reports.attendance.summary.absent", { defaultValue: "Absent" })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("reports.attendance.summary.excused", { defaultValue: "Excused" })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("reports.attendance.summary.late", { defaultValue: "Late" })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("reports.attendance.summary.total", { defaultValue: "Total" })}
                    </LdsTableHeadCell>
                    </LdsTableRow>
                  </LdsTableHead>
                  <LdsTableBody>
                  {filteredRows.map(({ classroomId, rawClassroomId, label, stats }) => {
                    const normalizedClassroomId = classroomId;
                    const isExpanded = expandedClassrooms.has(normalizedClassroomId);
                    const studentRows = studentStatsByClass.get(normalizedClassroomId) ?? [];
                    const childList = childrenByClass.get(normalizedClassroomId);
                    const isChildrenLoading = childrenLoading.has(normalizedClassroomId);
                    const childrenError = childrenErrors.get(normalizedClassroomId);
                    const childLookup = buildChildLookupMap(childList);
                    const enrichedStudentRows = studentRows
                      .map(({ studentKey, childId, stats: studentStats, sampleRecord, fallbackLabel }) => {
                        const labelCandidate =
                          buildStudentLabel(sampleRecord, t, childLookup) ||
                          fallbackLabel ||
                          (childId != null ? `#${childId}` : null) ||
                          t("reports.attendance.table.unknown_student", {
                            defaultValue: "Unknown student",
                          });
                        return {
                          studentKey,
                          childId,
                          stats: studentStats,
                          label: labelCandidate,
                        };
                      })
                      .sort((a, b) =>
                        a.label.localeCompare(b.label, undefined, {
                          numeric: true,
                          sensitivity: "base",
                        })
                      );
                    return (
                      <React.Fragment key={normalizedClassroomId}>
                        <LdsTableRow
                          className={
                            "border-b border-indigo-100/60 dark:border-gray-700 text-center transition hover:bg-indigo-50/40 dark:hover:bg-gray-800" +
                            (isExpanded ? " bg-indigo-50/50 dark:bg-gray-800/70" : "")
                          }
                        >
                          <LdsTableCell>
                            <button
                              type="button"
                              className="flex h-8 w-8 items-center justify-center rounded border border-gray-200 text-gray-600 transition hover:border-indigo-300 hover:text-indigo-500 focus:outline-none focus:ring focus:ring-indigo-400 focus:ring-offset-1 dark:border-gray-700 dark:text-gray-300 dark:hover:border-indigo-500 dark:hover:text-indigo-300 dark:focus:ring-indigo-500 dark:focus:ring-offset-gray-900"
                              onClick={() => toggleClassroomExpansion(normalizedClassroomId, rawClassroomId)}
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded
                                  ? t("reports.attendance.table.collapse_row", {
                                      defaultValue: "Collapse row",
                                    })
                                  : t("reports.attendance.table.expand_row", {
                                      defaultValue: "Expand row",
                                    })
                              }
                            >
                              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                          </LdsTableCell>
                          <LdsTableCell>{label}</LdsTableCell>
                          <LdsTableCell className="text-right">{stats.present}</LdsTableCell>
                          <LdsTableCell className="text-right">{stats.absent}</LdsTableCell>
                          <LdsTableCell className="text-right">{stats.excused}</LdsTableCell>
                          <LdsTableCell className="text-right">{stats.late}</LdsTableCell>
                          <LdsTableCell className="text-right font-bold">{stats.total}</LdsTableCell>
                        </LdsTableRow>
                        {isExpanded && (
                          <LdsTableRow>
                            <LdsTableCell colSpan={7} className="bg-slate-50 px-6 py-5 text-sm text-slate-800 dark:bg-slate-900/70 dark:text-slate-200">
                              {isChildrenLoading && !childList ? (
                                <div className="flex justify-center py-6">
                                  <Spinner />
                                </div>
                              ) : childrenError ? (
                                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/60 dark:bg-rose-500/10 dark:text-rose-200">
                                  {childrenError}
                                </div>
                              ) : enrichedStudentRows.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-indigo-100/70 px-4 py-6 text-center text-sm text-gray-500 dark:border-indigo-500/40 dark:text-gray-300">
                                  {t("reports.attendance.table.no_student_records", {
                                    defaultValue: "No student attendance records were found for this period.",
                                  })}
                                </div>
                              ) : (
                                <div className="rounded-2xl border border-indigo-100/70 bg-white/95 dark:border-gray-700/60 dark:bg-gray-900/70">
                                  <div className="max-w-4xl mx-auto w-full px-4 py-3">
                                    <ul className="columns-1 md:columns-2 gap-3 [column-fill:_balance]">
                                      {enrichedStudentRows.map(({ studentKey, label: studentLabel, stats: studentStats }) => (
                                        <StudentListItem
                                          key={studentKey}
                                          name={studentLabel}
                                          stats={studentStats}
                                          t={t}
                                          onClick={() => handleStudentClick(studentKey)}
                                          isActive={selectedStudent === studentKey}
                                        />
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </LdsTableCell>
                          </LdsTableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  </LdsTableBody>
                </LdsTable>
              </LdsTableScroll>
            </LdsTableCard>
          </>
        )}
        </div>
      </div>
    </main>
  );
};

export default ReportsAttendance;
